import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { Map } from 'immutable';
// components
import FormThemeProvider from 'contexts/FormThemeContext';
import StyledReferralFormContent from './ReferralFormContent';
// types
import { FormThemeValues } from 'assets/types';
// selectors
import getReferralForms from 'store/selectors/getReferralForms';
import getCurrentGroupId from 'store/selectors/getCurrentGroupId';
// action creators
import { fetchReferralFormById } from 'store/actions/referralFormActions';

// Outer component that provides the custom theme
const ReferralForm = () => {
  const dispatch = useDispatch();
  const { formId } = useParams();
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const token = urlParams.get('token');

  const forms = useSelector(getReferralForms);
  const currentGroupId = useSelector(getCurrentGroupId);

  const formData =
    forms.find(
      (form: TypedMap<ReferralForm>) => form.get('id')?.toString() === formId
    ) || Map<keyof ReferralForm, any>();

  const groupId = formData.get('groupId') || currentGroupId;

  useEffect(() => {
    // Checks if form data exists, refetches form if not
    if (!formData.size && formId && (token || groupId)) {
      dispatch(fetchReferralFormById({ formId, groupId, token }));
    }
  }, [groupId]); // eslint-disable-line

  const formTheme = useMemo((): FormThemeValues => {
    const theme = formData?.get ? formData.get('theme') : formData?.theme;
    if (!theme) return {};

    const themeJS = theme.toJS ? theme.toJS() : theme;

    return {
      primaryColor: themeJS.primaryColor,
      secondaryColor: themeJS.secondaryColor,
      backgroundColor: themeJS.backgroundColor,
      backgroundGradientEnabled: themeJS.backgroundGradientEnabled || false,
    };
  }, [formData]);

  return (
    <FormThemeProvider theme={formTheme}>
      <StyledReferralFormContent
        formData={formData}
        groupId={groupId}
        token={token}
      />
    </FormThemeProvider>
  );
};

export default ReferralForm;
